/**
 * OnlineStatusContext
 *
 * Provides online/offline status detection across the application.
 * Tracks connection state changes and provides indicators for when
 * connection is restored after being offline.
 *
 * Usage:
 *   Wrap your app with <OnlineStatusProvider>
 *   Use useOnlineStatus() hook to access connection state
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

interface OnlineStatusContextValue {
  /** Current online status */
  isOnline: boolean;
  /** Whether the user was offline at any point during this session */
  wasOffline: boolean;
  /** Whether the connection was just restored (useful for showing "back online" messages) */
  onlineRestored: boolean;
  /** Clear the onlineRestored flag after showing the message */
  clearOnlineRestored: () => void;
}

// ============================================================================
// Context
// ============================================================================

const OnlineStatusContext = createContext<OnlineStatusContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface OnlineStatusProviderProps {
  children: ReactNode;
}

export function OnlineStatusProvider({ children }: OnlineStatusProviderProps) {
  // Initialize with current online status
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Track if user has been offline during this session
  const [wasOffline, setWasOffline] = useState(false);

  // Track if connection was just restored (for showing "back online" message)
  const [onlineRestored, setOnlineRestored] = useState(false);

  // Handle connection changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // Only set onlineRestored if we were previously offline
      if (wasOffline) {
        setOnlineRestored(true);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      // Clear onlineRestored when going offline
      setOnlineRestored(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const clearOnlineRestored = () => {
    setOnlineRestored(false);
  };

  const value: OnlineStatusContextValue = {
    isOnline,
    wasOffline,
    onlineRestored,
    clearOnlineRestored,
  };

  return (
    <OnlineStatusContext.Provider value={value}>
      {children}
    </OnlineStatusContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access online status context
 *
 * @throws Error if used outside OnlineStatusProvider
 * @returns OnlineStatusContextValue with online status and helpers
 */
export function useOnlineStatus(): OnlineStatusContextValue {
  const context = useContext(OnlineStatusContext);

  if (!context) {
    throw new Error('useOnlineStatus must be used within OnlineStatusProvider');
  }

  return context;
}
